import {
  Camera,
  Check,
  MapPin,
  MapPinOff,
  RefreshCw,
  RotateCcw,
  SwitchCamera,
  TriangleAlert,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { describeCameraFailure } from '@/lib/camera';
import { drawStampedFrame, encodeJpeg, stampLines } from './punch-photo';

export interface PunchCaptureResult {
  photo: Blob;
  latitude: number | null;
  longitude: number | null;
  accuracyMeters: number | null;
  deviceReportedAt: string;
}

interface PunchCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Shown in the burnt-in stamp so a forwarded photo still names its subject. */
  employeeLabel: string;
  title: string;
  submitLabel: string;
  submitting: boolean;
  /**
   * Whether a punch without coordinates will be refused.
   *
   * Comes from the server so the two cannot disagree. The server refuses either
   * way — this is what stops somebody taking a photo, waiting through the
   * upload, and only then being told their location was off.
   */
  requiresLocation: boolean;
  onCapture: (result: PunchCaptureResult) => void;
}

type Facing = 'user' | 'environment';

interface Fix {
  latitude: number;
  longitude: number;
  accuracy: number;
}

/** The seconds a person has to get into frame before the shot is taken. */
const COUNTDOWN_SECONDS = 5;

/**
 * Three steps, so the photo on a punch is one the person chose.
 *
 *   - `live`: the camera preview, with a button to start the timer;
 *   - `counting`: a 5-second countdown over the preview, so there is time to
 *     stand back and be in frame before the shutter;
 *   - `review`: the frozen shot, to keep or retake. The punch is only sent
 *     from here, so a bad photo never reaches attendance.
 */
type Phase = 'live' | 'counting' | 'review';

export function PunchCaptureDialog({
  open,
  onOpenChange,
  employeeLabel,
  title,
  submitLabel,
  submitting,
  requiresLocation,
  onCapture,
}: PunchCaptureDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Rear camera by default: a whole-body shot needs someone else holding the
  // phone, and the rear lens is the better one on nearly every handset.
  const [facing, setFacing] = useState<Facing>('environment');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [fix, setFix] = useState<Fix | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);

  const [phase, setPhase] = useState<Phase>('live');
  const [count, setCount] = useState(COUNTDOWN_SECONDS);
  const [captured, setCaptured] = useState<PunchCaptureResult | null>(null);
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);

  // One punch per press. `onCapture` starts a mutation and `submitting` only
  // turns true on the next render, so a second tap in the same tick would send
  // a second punch and earn a "You have already clocked in today". The ref
  // closes that sub-render gap; it is released once the mutation settles.
  const submittedRef = useRef(false);
  useEffect(() => {
    if (!submitting) submittedRef.current = false;
  }, [submitting]);

  // The phase, readable from inside `takePhoto`'s async continuation, which
  // closed over the value at call time. Used to drop a shot whose encode
  // finishes after the countdown was cancelled or the dialog was closed —
  // otherwise that late write would resurrect a stale review, camera off.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // Read inside the geolocation callback, which `requestLocation` memoises with
  // an empty dependency list. A ref keeps the message current without making
  // the callback identity change on every render.
  const requiresLocationRef = useRef(requiresLocation);
  requiresLocationRef.current = requiresLocation;

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    // Clearing srcObject as well as stopping the tracks. The Android WebView
    // keeps the camera held open while a video element still references the
    // stream, so stopping the tracks alone leaves the lens light on — which is
    // exactly what this is meant to prevent, and what it looked like on a
    // handset even though the tracks were genuinely stopped.
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.srcObject = null;
    }
  }, []);

  // The camera runs for the preview and the countdown, and is released the
  // moment a shot is frozen for review — which is also the whole time the
  // photo is being looked at, uploaded on submit, or sitting on an error.
  // Retake sets the phase back to `live`, and this re-acquires it. The light
  // therefore never lingers, and there is no live lens during the upload.
  //
  // Keyed on this derived flag, not on `phase`: `live` and `counting` both
  // leave it true, so starting the countdown does not tear down and re-acquire
  // the stream — which would blank the framing preview at the worst moment.
  const cameraActive = open && phase !== 'review';
  useEffect(() => {
    if (!cameraActive) {
      stopStream();
      return;
    }

    let cancelled = false;

    void (async () => {
      setCameraError(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 1280 } },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        stopStream();
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
      } catch (error) {
        setCameraError(await describeCameraFailure(error));
      }
    })();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [cameraActive, facing, stopStream]);

  // A closed dialog forgets everything, so the next open starts on a live
  // preview rather than somebody else's frozen shot.
  useEffect(() => {
    if (!open) {
      setPhase('live');
      setCaptured(null);
      setCount(COUNTDOWN_SECONDS);
    }
  }, [open]);

  // The frozen shot, as a URL for the review image; revoked when it changes or
  // the dialog goes, so the bytes are not pinned.
  useEffect(() => {
    if (!captured) {
      setCapturedUrl(null);
      return;
    }
    const url = URL.createObjectURL(captured.photo);
    setCapturedUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [captured]);

  /**
   * Asks for a location twice: coarse first, then precise.
   *
   * This used to ask only for `enableHighAccuracy: true` with `maximumAge: 0`,
   * which forces the device to ignore any cached position and get a GPS fix.
   * Indoors — which is where people clock in — GPS frequently never fixes at
   * all, so the request ran its ten seconds and failed, and every punch was
   * stored with no coordinates.
   *
   * The coarse pass uses wifi and cell positioning, which answers in a second
   * or two indoors and is accurate to a building. That is enough to say
   * somebody was at the office rather than at home, which is the question this
   * feature exists to answer. The precise pass then runs in the background and
   * replaces the fix only if it genuinely arrives and is better.
   */
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('This browser cannot report a location.');
      return;
    }

    setLocating(true);
    setLocationError(null);

    const accept = (position: GeolocationPosition) => {
      setFix((current) => {
        const next = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };

        // The refining pass can land after a better fix is already showing, or
        // report a worse accuracy than the coarse one. Keep the better number.
        return current && current.accuracy <= next.accuracy ? current : next;
      });
      setLocating(false);
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        accept(position);

        // Refine quietly. A failure here is not an error — the coarse fix is
        // already recorded and is good enough.
        navigator.geolocation.getCurrentPosition(accept, () => {}, {
          enableHighAccuracy: true,
          timeout: 15_000,
          maximumAge: 0,
        });
      },
      (error) => {
        // What happens next depends on the setting, so the message has to. It
        // used to end "the punch is still recorded, without coordinates", which
        // became false the moment location was made mandatory — and a reassuring
        // sentence about a punch that is actually refused is worse than none.
        const consequence = requiresLocationRef.current
          ? 'You cannot clock in or out until it is on.'
          : 'The punch is still recorded, without coordinates.';

        // Same ambiguity as the camera: `geolocation=()` in Permissions-Policy
        // produces PERMISSION_DENIED without the browser ever prompting.
        setLocationError(
          error.code === error.PERMISSION_DENIED
            ? `Location was refused — either by this browser, or by the site itself. ${consequence}`
            : `No location fix yet — tap Retry, or step near a window. ${consequence}`,
        );
        setLocating(false);
      },
      // A minute-old fix is fine for a punch, and asking for a fresh one is
      // what made this fail.
      { enableHighAccuracy: false, timeout: 15_000, maximumAge: 60_000 },
    );
  }, []);

  useEffect(() => {
    if (open) requestLocation();
    else {
      setFix(null);
      setLocationError(null);
    }
  }, [open, requestLocation]);

  /** Draws the current frame with its stamp and freezes it for review. */
  const takePhoto = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) {
      // The lens was not ready when the timer ran out; back to the preview
      // rather than freezing a black frame.
      setPhase('live');
      return;
    }

    const at = new Date();
    drawStampedFrame(
      canvas,
      video,
      { width: video.videoWidth, height: video.videoHeight },
      stampLines(at, fix, employeeLabel),
    );

    try {
      const photo = await encodeJpeg(canvas);
      // Cancelled or dismissed while the frame was encoding: drop it rather
      // than jumping a closed or reset dialog into review.
      if (phaseRef.current !== 'counting') return;
      setCaptured({
        photo,
        latitude: fix?.latitude ?? null,
        longitude: fix?.longitude ?? null,
        accuracyMeters: fix ? Math.round(fix.accuracy) : null,
        deviceReportedAt: at.toISOString(),
      });
      setPhase('review');
    } catch (error) {
      setCameraError(error instanceof Error ? error.message : 'Could not save the photo.');
      setPhase('live');
    }
  }, [employeeLabel, fix]);

  // The countdown itself: a tick a second while counting, then the shot.
  //
  // Reached through a ref so the timer does not depend on `takePhoto`: its
  // identity changes when the location fix updates, and a fix landing mid-count
  // would otherwise reset the current second's tick.
  const takePhotoRef = useRef(takePhoto);
  takePhotoRef.current = takePhoto;
  useEffect(() => {
    if (phase !== 'counting') return;
    if (count <= 0) {
      void takePhotoRef.current();
      return;
    }
    const timer = setTimeout(() => setCount((current) => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [phase, count]);

  const locationBlocks = requiresLocation && !fix;

  const startCountdown = () => {
    if (phase !== 'live' || cameraError || locationBlocks) return;
    setCount(COUNTDOWN_SECONDS);
    setPhase('counting');
  };

  const cancelCountdown = () => {
    setPhase('live');
    setCount(COUNTDOWN_SECONDS);
  };

  const retake = () => {
    setCaptured(null);
    setPhase('live');
  };

  const submit = () => {
    if (!captured || submitting || submittedRef.current || locationBlocks) return;
    submittedRef.current = true;
    onCapture(captured);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {phase === 'review'
              ? 'Check the photo. Retake it if it is unclear, then send it.'
              : 'Stand far enough back that you are visible head to foot. A five-second timer gives you time to get in frame; the time and your coordinates are printed onto the photo.'}
          </DialogDescription>
        </DialogHeader>

        {cameraError ? (
          <Alert variant="destructive">
            <TriangleAlert aria-hidden="true" />
            <AlertDescription>{cameraError}</AlertDescription>
          </Alert>
        ) : (
          <div className="relative overflow-hidden rounded-lg bg-muted">
            {phase === 'review' && capturedUrl ? (
              <img
                src={capturedUrl}
                alt="The photo you are about to send"
                className="aspect-[3/4] w-full object-cover"
              />
            ) : (
              <video
                ref={videoRef}
                playsInline
                muted
                className="aspect-[3/4] w-full object-cover"
                aria-label="Camera preview"
              />
            )}

            {phase === 'live' && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="absolute right-2 top-2"
                onClick={() => setFacing((current) => (current === 'user' ? 'environment' : 'user'))}
              >
                <SwitchCamera aria-hidden="true" />
                Flip
              </Button>
            )}

            {phase === 'counting' && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-black/40"
                role="status"
                aria-live="assertive"
              >
                <span className="text-7xl font-bold tabular-nums text-white drop-shadow-lg">
                  {count}
                </span>
                <span className="sr-only">
                  Taking the photo in {count} second{count === 1 ? '' : 's'}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-start gap-2 text-sm">
          <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            {fix ? (
              <p className="tabular-nums">
                {fix.latitude.toFixed(6)}, {fix.longitude.toFixed(6)}{' '}
                <span className="text-muted-foreground">±{Math.round(fix.accuracy)} m</span>
              </p>
            ) : (
              <p className="text-muted-foreground">
                {locating ? 'Finding your location…' : (locationError ?? 'No location yet.')}
              </p>
            )}
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={requestLocation} loading={locating}>
            <RefreshCw aria-hidden="true" />
            Retry
          </Button>
        </div>

        <canvas ref={canvasRef} className="hidden" />

        {/*
          Says what is wrong and what to do about it, while the camera is still
          open. The server refuses this punch anyway; being told here saves
          taking a photo and waiting through an upload to find out.
        */}
        {locationBlocks && !locating && (
          <Alert variant="destructive">
            <MapPinOff aria-hidden="true" />
            <AlertDescription>
              {locationError
                ? `${locationError} Location is required to ${submitLabel.toLowerCase()}.`
                : `Location is required to ${submitLabel.toLowerCase()}. Turn on location for your browser, then try again.`}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          {phase === 'review' ? (
            <>
              <Button variant="outline" onClick={retake} disabled={submitting}>
                <RotateCcw aria-hidden="true" />
                Retake
              </Button>
              <Button
                onClick={submit}
                loading={submitting}
                // Blocked without a fix when one is required: the alternative is
                // a button that looks ready and fails every time it is pressed.
                disabled={locationBlocks}
                title={locationBlocks ? 'Waiting for your location' : undefined}
              >
                <Check aria-hidden="true" />
                {submitLabel}
              </Button>
            </>
          ) : phase === 'counting' ? (
            <>
              {/* Disabled once the shot has begun (count 0), so a Cancel in the
                  encode window cannot fight the frame that is already taken. */}
              <Button variant="outline" onClick={cancelCountdown} disabled={count <= 0}>
                Cancel
              </Button>
              <Button disabled>
                <Camera aria-hidden="true" />
                Taking photo… {count}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={startCountdown}
                disabled={Boolean(cameraError) || locationBlocks}
                title={locationBlocks ? 'Waiting for your location' : undefined}
              >
                <Camera aria-hidden="true" />
                Take photo
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
